// ===========================================
// Drive File Uploader Component
// ===========================================
// File uploader that syncs to Google Drive

import { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Alert,
  LinearProgress,
  Stack,
  SelectChangeEvent,
  Autocomplete,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  Cloud as DriveIcon,
  FolderOff as FolderOffIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import {
  FileVisibility,
  getVisibilityLabel,
  getVisibilityColor,
  formatFileSize,
  isAllowedFileType,
  isFileSizeAllowed,
  MAX_FILE_SIZE,
} from '../../api/resources.api';
import { useUploadDriveFile, useFolderMappings } from '../../hooks/useGoogleDrive';
import { useLessons } from '../../hooks/useLessons';
import { useStudents } from '../../hooks/useUsers';
import { Student } from '../../api/users.api';
import { useSendMessage, useRecipients } from '../../hooks/useMessages';
import { useLocation, useNavigate } from 'react-router-dom';
import { viewFromPath } from '../common/breadcrumbRoot';

// ===========================================
// TYPES
// ===========================================

export interface DriveFileUploaderProps {
  lessonId?: string;
  studentId?: string;
  onUploaded?: () => void;
  onClose?: () => void;
  open?: boolean; // When true, shows as dialog; when undefined, shows inline
  /**
   * The class picker lists only the caller's own classes (`mine`). Default OFF: the admin
   * pages list every class. The teacher Files page sets it (roles R12, slice 1b §5.12).
   */
  ownClassesOnly?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function DriveFileUploader({
  lessonId: propLessonId,
  studentId: propStudentId,
  onUploaded,
  onClose,
  open = true,
  ownClassesOnly = false,
}: DriveFileUploaderProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Determine if we need to show selection UI
  const needsSelection = !propLessonId && !propStudentId;

  // State - must be declared before any conditional returns
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [visibility, setVisibility] = useState<FileVisibility>('ALL');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadTarget, setUploadTarget] = useState<'lesson' | 'student'>('lesson');
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);

  // Queries for selection - only fetch when needed
  const { data: folderMappings, isLoading: mappingsLoading } = useFolderMappings();
  const { data: allLessons, isLoading: lessonsLoading } = useLessons(ownClassesOnly ? { mine: true } : undefined);
  const { data: allStudents, isLoading: studentsLoading } = useStudents();

  // Filter to only lessons/students that have linked folders. Match on the scalar
  // FKs: this read `m.lesson`/`m.student` (never sent — the API's keys are
  // PascalCase), so every set was empty and the uploader always said "No Folders
  // Linked Yet" (#202).
  const linkedLessonIds = new Set(
    (folderMappings ?? []).map((m) => m.lessonId).filter((id): id is string => !!id)
  );
  const linkedStudentIds = new Set(
    (folderMappings ?? []).map((m) => m.studentId).filter((id): id is string => !!id)
  );

  const lessonsWithFolders = allLessons?.filter(l => linkedLessonIds.has(l.id)) || [];
  const studentsWithFolders: Student[] = allStudents?.filter((s: Student) => linkedStudentIds.has(s.id)) || [];

  const hasNoLinkedFolders = !mappingsLoading && lessonsWithFolders.length === 0 && studentsWithFolders.length === 0;

  // Determine final lessonId/studentId to use
  const lessonId = propLessonId || selectedLessonId || undefined;
  const studentId = propStudentId || selectedStudentId || undefined;

  // Mutations
  const uploadMutation = useUploadDriveFile();
  const sendMessage = useSendMessage();
  const { data: recipients } = useRecipients();

  // The office is who links folders: an Admin that is not a support account, or staff who handle
  // messages (roles slice 2, #310), never "the first ADMIN" (that could be a support account).
  const officeRecipient = recipients?.find((r) => r.isOffice);

  // Handle drag events - must be declared before any conditional returns
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      const validFiles: File[] = [];
      const errors: string[] = [];

      droppedFiles.forEach((file) => {
        if (!isAllowedFileType(file.type)) {
          errors.push(`${file.name}: Invalid file type`);
        } else if (!isFileSizeAllowed(file.size)) {
          errors.push(`${file.name}: Exceeds max size (${formatFileSize(MAX_FILE_SIZE)})`);
        } else {
          validFiles.push(file);
        }
      });

      if (errors.length > 0) {
        setError(errors.join('. '));
      } else {
        setError(null);
      }

      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
      }
    }
  }, []);

  // If open is false, don't render (after all hooks)
  if (!open) return null;

  // Handle notify admin
  const handleNotifyAdmin = async () => {
    if (!officeRecipient) {
      setError('No one in the office can be messaged. Please contact support.');
      return;
    }
    try {
      await sendMessage.mutateAsync({
        recipientId: officeRecipient.id,
        content: `Hi,\n\nI'd like to upload files for my classes but there are no Google Drive folders linked yet.\n\nCould you please set up folder links for my classes so I can share resources with students?\n\nThank you!`,
      });
      onClose?.();
      // To Messages in the view the user is IN, under the school slug (#310: this was a slugless
      // path chosen by the login role, which rendered NotFound).
      const [, slug] = pathname.split('/');
      navigate(`/${slug}/${viewFromPath(pathname) ?? 'admin'}/messages`);
    } catch {
      setError('Failed to send the message. Please try again.');
    }
  };

  // Handle file selection (multiple files)
  const handleFileSelect = (files: File[]) => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach((file) => {
      if (!isAllowedFileType(file.type)) {
        errors.push(`${file.name}: Invalid file type`);
      } else if (!isFileSizeAllowed(file.size)) {
        errors.push(`${file.name}: Exceeds max size (${formatFileSize(MAX_FILE_SIZE)})`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setError(errors.join('. '));
    } else {
      setError(null);
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  // Handle file input change
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelect(Array.from(files));
    }
    // Reset input so same file can be selected again
    event.target.value = '';
  };

  // Handle visibility change
  const handleVisibilityChange = (event: SelectChangeEvent) => {
    setVisibility(event.target.value as FileVisibility);
  };

  // Handle tag input
  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = tagInput.trim();
      if (tag && !tags.includes(tag) && tags.length < 10) {
        setTags([...tags, tag]);
        setTagInput('');
      }
    }
  };

  // Remove tag
  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  // Remove a specific file from the selection
  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Clear all selected files
  const handleClearAllFiles = () => {
    setSelectedFiles([]);
    setError(null);
  };

  // Handle upload (batch)
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    if (!lessonId && !studentId) {
      setError('Please select a class or student to upload the files to.');
      return;
    }

    setUploadProgress({ current: 0, total: selectedFiles.length });

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        setUploadProgress({ current: i + 1, total: selectedFiles.length });

        await uploadMutation.mutateAsync({
          file: selectedFiles[i],
          lessonId,
          studentId,
          visibility,
          tags: tags.length > 0 ? tags : undefined,
        });
      }

      // Reset form
      setSelectedFiles([]);
      setTags([]);
      setTagInput('');
      setVisibility('ALL');
      setUploadProgress(null);

      onUploaded?.();
    } catch {
      setUploadProgress(null);
      // Error is handled by the mutation's onError
    }
  };

  // Show no-folders message when there are no linked folders
  if (needsSelection && hasNoLinkedFolders) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <FolderOffIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No Folders Linked Yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Before you can upload files, the office needs to link Google Drive folders to your classes or students.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          {onClose && (
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={sendMessage.isPending ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
            onClick={handleNotifyAdmin}
            disabled={sendMessage.isPending || !officeRecipient}
          >
            {sendMessage.isPending ? 'Sending...' : 'Ask the office'}
          </Button>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <DriveIcon color="primary" />
        <Typography variant="h6">Upload to Google Drive</Typography>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 2 }}>
        Files uploaded here will automatically sync to the linked Google Drive folder.
      </Alert>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Target Selection - Only show when no lessonId/studentId provided */}
      {needsSelection && (
        <Box sx={{ mb: 3 }}>
          {/* Target type toggle */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Upload To</InputLabel>
            <Select
              value={uploadTarget}
              onChange={(e) => {
                setUploadTarget(e.target.value as 'lesson' | 'student');
                setSelectedLessonId(null);
                setSelectedStudentId(null);
              }}
              label="Upload To"
            >
              <MenuItem value="lesson">Class</MenuItem>
              <MenuItem value="student">Student</MenuItem>
            </Select>
          </FormControl>

          {/* Lesson/Student selector */}
          {uploadTarget === 'lesson' ? (
            <Autocomplete
              options={lessonsWithFolders}
              getOptionLabel={(option) => option.name}
              loading={lessonsLoading}
              value={lessonsWithFolders.find(l => l.id === selectedLessonId) || null}
              onChange={(_, value) => setSelectedLessonId(value?.id || null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Class"
                  placeholder="Choose a class..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {lessonsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.LessonType?.name} • {option.Instructor?.User?.firstName} {option.Instructor?.User?.lastName}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          ) : (
            <Autocomplete
              options={studentsWithFolders}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              loading={studentsLoading}
              value={studentsWithFolders.find((s: Student) => s.id === selectedStudentId) || null}
              onChange={(_, value) => setSelectedStudentId(value?.id || null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Student"
                  placeholder="Choose a student..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {studentsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Typography variant="body2">
                    {option.firstName} {option.lastName}
                  </Typography>
                </li>
              )}
            />
          )}
        </Box>
      )}

      {/* Drag and Drop Zone */}
      <Box
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: selectedFiles.length > 0 ? 2 : 4,
          textAlign: 'center',
          bgcolor: isDragging ? 'action.hover' : 'background.default',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: selectedFiles.length > 0 ? 80 : 200,
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}
        component="label"
      >
        <input
          type="file"
          hidden
          multiple
          onChange={handleFileInputChange}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp,.mp3,.wav,.ogg,.m4a,.mp4,.mpeg,.webm,.mov"
        />
        <CloudUploadIcon sx={{ fontSize: selectedFiles.length > 0 ? 32 : 48, color: 'primary.main', mb: selectedFiles.length > 0 ? 0.5 : 2 }} />
        {selectedFiles.length === 0 ? (
          <>
            <Typography variant="h6" gutterBottom>
              Drag & drop files here
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              or click to browse (multiple files supported)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Max file size: {formatFileSize(MAX_FILE_SIZE)}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Click or drop to add more files
          </Typography>
        )}
      </Box>

      {/* Selected Files Preview */}
      {selectedFiles.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">
              {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
            </Typography>
            <Button
              size="small"
              color="error"
              onClick={handleClearAllFiles}
            >
              Clear All
            </Button>
          </Box>
          <Stack spacing={1} sx={{ maxHeight: 200, overflow: 'auto' }}>
            {selectedFiles.map((file, index) => (
              <Box
                key={`${file.name}-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                }}
              >
                <FileIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveFile(index)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Visibility Selector */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Visibility</InputLabel>
        <Select value={visibility} onChange={handleVisibilityChange} label="Visibility">
          <MenuItem value="ALL">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getVisibilityLabel('ALL')}
              <Chip
                label="Students, Parents, Teachers"
                size="small"
                color={getVisibilityColor('ALL')}
              />
            </Box>
          </MenuItem>
          <MenuItem value="TEACHERS_AND_PARENTS">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getVisibilityLabel('TEACHERS_AND_PARENTS')}
              <Chip
                label="Parents & Teachers only"
                size="small"
                color={getVisibilityColor('TEACHERS_AND_PARENTS')}
              />
            </Box>
          </MenuItem>
          <MenuItem value="TEACHERS_ONLY">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getVisibilityLabel('TEACHERS_ONLY')}
              <Chip
                label="Teachers only"
                size="small"
                color={getVisibilityColor('TEACHERS_ONLY')}
              />
            </Box>
          </MenuItem>
        </Select>
      </FormControl>

      {/* Tags Input */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Tags"
          placeholder="Type a tag and press Enter"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleTagInputKeyDown}
          helperText={`${tags.length}/10 tags`}
          disabled={tags.length >= 10}
        />
        {tags.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onDelete={() => handleRemoveTag(tag)}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* Upload Progress */}
      {(uploadMutation.isPending || uploadProgress) && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress
            variant={uploadProgress ? 'determinate' : 'indeterminate'}
            value={uploadProgress ? (uploadProgress.current / uploadProgress.total) * 100 : undefined}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {uploadProgress
              ? `Uploading file ${uploadProgress.current} of ${uploadProgress.total}...`
              : 'Uploading and syncing to Google Drive...'}
          </Typography>
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        {onClose && (
          <Button variant="outlined" onClick={onClose} disabled={uploadMutation.isPending}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleUpload}
          disabled={selectedFiles.length === 0 || uploadMutation.isPending}
        >
          {uploadMutation.isPending
            ? `Uploading ${uploadProgress?.current || 1} of ${selectedFiles.length}...`
            : selectedFiles.length === 1
              ? 'Upload to Drive'
              : `Upload ${selectedFiles.length} Files`}
        </Button>
      </Box>
    </Paper>
  );
}
